// @ts-nocheck
import React, { useCallback } from 'react';

import { useDebouncedInput } from '../../../../hooks/useDebouncedInput';
import { IconButton, InputAdornment, TextField, Tooltip } from '@mui/material';
import PropTypes from 'prop-types';
import { ReactComponent as LockIcon } from '../../../../assets/Icons/lucideIcons/lock.svg';

import {
  blockInvalidNumbers,
  getErrorHelperText,
  isInvalidNumber,
  isValueEmpty,
} from '../../../../utils/Utils';
import { ReactComponent as CalculationIcon } from '../../../../assets/images/CalculationIcon.svg';
import { composeDraftBlurHandler } from '../../../../hooks/useDraftAutoSave.jsx';
import { DRAFT_INPUT_SX, INPUT_LABEL_PROPS } from './TextFieldComponent';

/**
 * Integer field component for editing numeric attributes
 * @param {Object} props - Component props
 * @param {string} props.label - Field label
 * @param {string|number} props.value - Current field value
 * @param {Function} props.onChange - Function to handle value changes
 * @param {Object} props.attribute - The attribute being edited
 * @param {Object} props.entityDetailsFormikForm - Formik form instance for error handling
 * @param {boolean} props.required - Whether the field is required
 * @returns {React.ReactElement} The TextField component with number type
 */
const IntegerFieldComponent = ({
  label,
  value,
  onChange,
  attribute,
  titleLabel,
  entityDetailsFormikForm,
  required = false,
  handleOpenDerivedModal,
  hideHeader,
  isLoadingConstraints = false,
  hasDraftChip = false,
  onDraftBlur,
}) => {
  const isDisabled = !attribute?.is_editable;
  const isDerived = hideHeader
    ? attribute?.is_derived && attribute?.permission === 'manage'
    : attribute?.is_derived;

  const commitValue = useCallback(
    (inputValue) => {
      if (isValueEmpty(inputValue)) {
        onChange(null);
      } else {
        onChange(Number(inputValue));
      }
    },
    [onChange]
  );

  const { localValue, setValue, flush } = useDebouncedInput(
    value ?? '',
    commitValue
  );

  const { onBlur: formikBlur, ...formikFieldProps } =
    entityDetailsFormikForm?.getFieldProps(attribute?.id) || {};

  const handleChange = useCallback(
    (e) => {
      const inputValue = e?.target?.value;

      if (!entityDetailsFormikForm?.touched?.[attribute?.id]) {
        entityDetailsFormikForm?.setFieldTouched(attribute?.id, true, false);
      }

      setValue(inputValue);
    },
    [setValue, entityDetailsFormikForm, attribute?.id]
  );

  const handleBlur = useCallback(
    (event) => {
      flush();
      composeDraftBlurHandler(formikBlur, onDraftBlur)(event);
    },
    [flush, formikBlur, onDraftBlur]
  );

  /**
   * Handles keyboard events to block invalid characters for number inputs
   * @function
   * @param {React.KeyboardEvent} e - The keyboard event
   * @returns {void}
   */

  const handleKeyDown = useCallback(
    (e) =>
      ['Integer'].includes(attribute?.data_type)
        ? blockInvalidNumbers(e, attribute?.data_type)
        : null,
    [blockInvalidNumbers, attribute]
  );

  /**
   * Prevents wheel events from changing the numeric input value
   * @function
   * @param {React.WheelEvent} e - The wheel event
   * @returns {void}
   */
  const handleWheel = useCallback((e) => {
    // Prevent the input value change on scroll
    e?.target?.blur();
  }, []);

  const handleClickAttribute = useCallback(
    (e) => {
      e.stopPropagation();
      handleOpenDerivedModal(attribute);
    },
    [handleOpenDerivedModal, attribute]
  );
  const getIntegerInputProps = useCallback(() => {
    if (attribute?.data_type !== 'Integer') return {};

    return {
      inputProps: {
        min: 0,
        step: 1,
      },
    };
  }, [attribute?.data_type]);
  const renderEndAdornment = useCallback(() => {
    return (
      <InputAdornment position="end">
        {isDerived ? (
          <Tooltip
            title={
              !attribute?.show_calculation_popUp
                ? 'Enable the calculation popup to calculate this attribute.'
                : `Calculate ${attribute?.label}`
            }
          >
            <IconButton
              className="disabled-svg-icon"
              sx={{ p: 0 }}
              onClick={handleClickAttribute}
              disabled={!attribute?.show_calculation_popUp}
            >
              <CalculationIcon />
            </IconButton>
          </Tooltip>
        ) : (
          isDisabled && (
            <Tooltip title="Field is locked or not editable">
              <LockIcon />
            </Tooltip>
          )
        )}
      </InputAdornment>
    );
  }, [isDerived, attribute?.label, handleClickAttribute, isDisabled]);

  return (
    <TextField
      {...formikFieldProps}
      type={isInvalidNumber(localValue) ? 'text' : 'number'}
      label={label}
      title={typeof label === 'string' ? titleLabel : undefined}
      value={localValue}
      placeholder={isDerived ? '-' : 'Enter a value'}
      disabled={isDisabled || isDerived}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      onWheel={handleWheel}
      fullWidth
      variant="outlined"
      size="medium"
      required={required}
      error={Boolean(
        getErrorHelperText(
          entityDetailsFormikForm?.errors[attribute?.id],
          entityDetailsFormikForm?.touched[attribute?.id]
        )
      )}
      helperText={getErrorHelperText(
        entityDetailsFormikForm?.errors[attribute?.id],
        entityDetailsFormikForm?.touched[attribute?.id]
      )}
      slotProps={{
        input: {
          ...getIntegerInputProps(),
          readOnly: isLoadingConstraints,
          endAdornment: renderEndAdornment(),
        },
        inputLabel: INPUT_LABEL_PROPS,
      }}
      sx={hasDraftChip ? DRAFT_INPUT_SX : undefined}
    />
  );
};

IntegerFieldComponent.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  required: PropTypes.bool,
  attribute: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    data_type: PropTypes.oneOf(['Integer', 'Decimal']),
    is_editable: PropTypes.bool,
    label: PropTypes.string,
    is_derived: PropTypes.string,
    permission: PropTypes.string,
    show_calculation_popUp: PropTypes.bool,
  }).isRequired,
  entityDetailsFormikForm: PropTypes.shape({
    getFieldProps: PropTypes.func,
    errors: PropTypes.objectOf(PropTypes.string),
    touched: PropTypes.objectOf(PropTypes.bool),
    setFieldTouched: PropTypes.func,
  }),
  titleLabel: PropTypes.string.isRequired,
  handleOpenDerivedModal: PropTypes.func.isRequired,
  hideHeader: PropTypes.bool.isRequired,
  isLoadingConstraints: PropTypes.bool,
  hasDraftChip: PropTypes.bool,
  onDraftBlur: PropTypes.func,
};

export default IntegerFieldComponent;
